import { OverlayRef } from '@angular/cdk/overlay';
import { InjectionToken, signal, WritableSignal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Token d'injection pour les données du dialogue
 * Équivalent à MAT_DIALOG_DATA de Material
 */
export const WIZY_DIALOG_DATA = new InjectionToken<any>('WizyDialogData');

/**
 * Configuration du dialogue
 */
export interface WizyDialogConfig<D = any> {
    /**
     * Données à passer au composant du dialogue
     */
    data?: D;

    /**
     * Taille du dialogue
     */
    size?: 'sm' | 'md' | 'lg' | 'full';

    /**
     * Type de dialogue
     */
    type?: 'default' | 'danger' | 'warning';

    /**
     * Afficher le header
     */
    hasHeader?: boolean;

    /**
     * Afficher le footer
     */
    hasFooter?: boolean;

    /**
     * Empêcher la fermeture en cliquant sur le backdrop
     */
    disableClose?: boolean;

    /**
     * Classes CSS personnalisées à ajouter au dialogue
     */
    panelClass?: string | string[];

    /**
     * Largeur du dialogue (pour compatibilité MatDialog)
     */
    width?: string | number;

    /**
     * Hauteur du dialogue (pour compatibilité MatDialog)
     */
    height?: string | number;

    /**
     * Largeur maximale du dialogue (pour compatibilité MatDialog)
     */
    maxWidth?: string | number;

    /**
     * Hauteur maximale du dialogue (pour compatibilité MatDialog)
     */
    maxHeight?: string | number;

    /**
     * Largeur minimale du dialogue
     */
    minWidth?: string | number;

    /**
     * Hauteur minimale du dialogue
     */
    minHeight?: string | number;
}

/**
 * Référence à un dialogue ouvert
 * Équivalent à MatDialogRef de Material
 */
export class WizyDialogRef<T, R = any> {
    // Subject pour maintenir la compatibilité avec afterClosed() qui retourne un Observable
    private readonly afterClosedSubject = new Subject<R | undefined>();

    // Signal pour l'état d'ouverture
    private readonly isOpenSignal: WritableSignal<boolean> = signal(true);

    // Signal pour le résultat
    private readonly resultSignal: WritableSignal<R | undefined> = signal(undefined);

    /**
     * Observable qui émet quand le dialogue est fermé (pour compatibilité MatDialog)
     */
    readonly afterClosed$: Observable<R | undefined> = this.afterClosedSubject.asObservable();

    /**
     * Signal readonly pour l'état d'ouverture
     */
    readonly isOpen = this.isOpenSignal.asReadonly();

    /**
     * Signal readonly pour le résultat
     */
    readonly result = this.resultSignal.asReadonly();

    /**
     * Configuration du dialogue
     */
    disableClose = true;

    /**
     * Signal pour les classes CSS du panel
     */
    private readonly panelClassesSignal: WritableSignal<string[]> = signal([]);
    readonly panelClasses = this.panelClassesSignal.asReadonly();

    // Callback de fermeture (nullable pour éviter les memory leaks)
    private closeCallback: (() => void) | null;

    // Référence à l'overlay (nullable, optionnel pour compatibilité)
    private overlayRef: OverlayRef | null = null;

    // Flag pour savoir si le dialogue a déjà été fermé
    private isClosed = false;

    constructor(closeCallback: () => void, overlayRef?: OverlayRef) {
        this.closeCallback = closeCallback;
        this.overlayRef = overlayRef || null;
    }

    /**
     * Ferme le dialogue avec un résultat optionnel
     */
    close(result?: R): void {
        // Éviter les fermetures multiples
        if (this.isClosed) {
            return;
        }

        this.isClosed = true;
        this.resultSignal.set(result);
        this.isOpenSignal.set(false);

        // Appeler le callback de fermeture
        if (this.closeCallback) {
            this.closeCallback();
        }

        // Émettre et compléter le Subject
        this.afterClosedSubject.next(result);
        this.afterClosedSubject.complete();

        // Nettoyer la référence du callback pour éviter les memory leaks
        this.cleanup();
    }

    /**
     * Nettoie les références pour éviter les memory leaks
     * @internal
     */
    private cleanup(): void {
        this.closeCallback = null;
    }

    /**
     * Détruit le dialogue et nettoie toutes les références
     * Utile pour forcer le nettoyage en cas d'erreur
     */
    destroy(): void {
        if (!this.isClosed) {
            this.close();
        }
        this.cleanup();
    }

    /**
     * Observable pour la fermeture du dialogue
     * Méthode pour compatibilité avec MatDialog
     */
    afterClosed(): Observable<R | undefined> {
        return this.afterClosed$;
    }

    /**
     * Ajoute une classe CSS au panel du dialogue
     */
    addPanelClass(className: string | string[]): void {
        const classes = Array.isArray(className) ? className : [className];
        const currentClasses = this.panelClassesSignal();
        const newClasses = [...currentClasses];

        classes.forEach((cls) => {
            if (!newClasses.includes(cls)) {
                newClasses.push(cls);
            }
        });

        this.panelClassesSignal.set(newClasses);

        // Appliquer les classes directement sur l'overlay si disponible
        if (this.overlayRef) {
            classes.forEach((cls) => {
                this.overlayRef!.addPanelClass(cls);
            });
        }
    }

    /**
     * Retire une classe CSS du panel du dialogue
     */
    removePanelClass(className: string | string[]): void {
        const classes = Array.isArray(className) ? className : [className];
        const currentClasses = this.panelClassesSignal();
        const newClasses = currentClasses.filter((cls) => !classes.includes(cls));
        this.panelClassesSignal.set(newClasses);

        // Retirer les classes directement de l'overlay si disponible
        if (this.overlayRef) {
            classes.forEach((cls) => {
                this.overlayRef!.removePanelClass(cls);
            });
        }
    }
}
